import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { OrbisLogo } from '@/components/OrbisLogo';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast.error('Invalid reset link');
      navigate('/login');
      return;
    }
    setToken(tokenParam);
  }, [searchParams, navigate]);

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/\d/.test(password)) return 'Password must contain at least one number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      toast.success(response.data.message);
      navigate('/login');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: 'var(--orbis-bg)' }}>
      <div className="w-full max-w-[400px]">
        <OrbisLogo size={32} variant="full" className="mx-auto mb-8" />

        <div className="mb-6">
          <h2 style={{ fontSize: '24px', fontWeight: 200, letterSpacing: '-0.04em', color: 'var(--orbis-warm)', marginBottom: '8px' }}>
            Set new password
          </h2>
          <p style={{ fontSize: '14px', fontWeight: 300, color: '#5A5855' }}>
            Enter your new password below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <label style={{ fontSize: '11px', fontWeight: 400, fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5A5855' }}>
              New Password
            </label>
            <div className="flex items-center gap-3 px-4" style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '10px', height: '48px' }}>
              <Lock className="w-4 h-4 flex-shrink-0" style={{ color: '#5A5855' }} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none"
                style={{ fontSize: '14px', fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="flex-shrink-0 hover:opacity-70"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" style={{ color: '#5A5855' }} />
                ) : (
                  <Eye className="w-4 h-4" style={{ color: '#5A5855' }} />
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label style={{ fontSize: '11px', fontWeight: 400, fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5A5855' }}>
              Confirm Password
            </label>
            <div className="flex items-center gap-3 px-4" style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '10px', height: '48px' }}>
              <Lock className="w-4 h-4 flex-shrink-0" style={{ color: '#5A5855' }} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none"
                style={{ fontSize: '14px', fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="flex-shrink-0 hover:opacity-70"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-4 h-4" style={{ color: '#5A5855' }} />
                ) : (
                  <Eye className="w-4 h-4" style={{ color: '#5A5855' }} />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center justify-center gap-2 w-full transition-opacity"
            style={{
              height: '48px', borderRadius: '100px', border: 'none',
              background: 'var(--orbis-blue)', color: 'var(--orbis-bg)',
              fontSize: '14px', fontWeight: 400, fontFamily: 'var(--orbis-font)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              marginTop: '4px',
            }}
          >
            {isLoading ? (
              <><OrbisSpinner size={16} color="var(--orbis-bg)" />Resetting password...</>
            ) : (
              <>Reset password</>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 text-sm hover:underline"
            style={{ color: 'var(--orbis-warm)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
}